
import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { catchError, of } from 'rxjs';
import { Company, CompanyInfo, GroupedPermissions, PermissionNameAndValue, ProductPermissions, Role } from '../interfaces/companies';
import { User } from '../interfaces/users';
import { ConfigService } from './config-service.service';
import { LoadingScreenService } from './loading-screen.service';
import { RestApiService } from './restApiService.service';




@Injectable({
  providedIn: 'root'
})
export class UserManagementService {
  url: String;
  companiesLoaded = false;
  companies: Company[] = [];
  companyMap: {
    [code: string]: Company;
  } = {};
  companyRoleProductMap: {
    [code: string]: {
      [role: string]: string[];
    };
  } = {};

  usersLoaded = false;
  userMap: {
    [email: string]: User;
  } = {};
  companyUserMap: {
    [code: string]: User[];
  } = {};

  isInitialized = false;

  permissionsLoaded = false;
  permissionsForProducts: ProductPermissions[] = [];

  constructor(
    private restApi: RestApiService,
    private config: ConfigService,
    public loadingService: LoadingScreenService,
    private messageService: MessageService
  ) {
    const configuration = this.config.getJson();
    this.url = configuration.backendUrl;
  }


  async init() {
    if (!this.isInitialized) {
      this.loadingService.startLoading();
      const promiseArray = [
        this.fetchProductPermissions(),
        this.fetchCompanies(),
        this.fetchUsers()
      ];

      await Promise.all(promiseArray);
      this.loadingService.stopLoading();
      this.isInitialized = true;
    }
  }

  fetchProductPermissions() {
    return new Promise(resolve => {
      this.restApi.getPermissions().subscribe(productPermissionResponses => {
        this.permissionsLoaded = true;
        for (let productResponse of productPermissionResponses) {
          this.permissionsForProducts.push({ product: productResponse.product, permissions: productResponse.permissions.map(p => productResponse.product + ":" + p) });
        }
        resolve(true);
      });
    });
  }

  fetchCompanies() {
    return new Promise(resolve => {
      this.restApi.getCompanies().subscribe(response => {
        const companyArray: Company[] = [];
        const companyRoleProductMap: Record<string, Record<string, string[]>> = {};
        for (const code in response) {
          const respCompany = response[code];
          companyRoleProductMap[code] = {};

          const company = new Company(code, respCompany.name, respCompany.products, []);

          for (const roleName in respCompany.roles) {
            company.addRole({
              name: roleName,
              company: {
                code: code,
                name: respCompany.name
              },
              permissions: respCompany.roles[roleName].permissions,
              products: respCompany.roles[roleName].products
            });

            companyRoleProductMap[code][roleName] = [];
          }

          companyArray.push(company);
          this.companyMap[code] = company;

        }

        this.companies = companyArray;
        this.companiesLoaded = true;
        resolve(true);
      });
    });
  }

  fetchUsers() {
    return new Promise(resolve => {
      this.restApi.getUsers().subscribe(response => {
        this.usersLoaded = true;
        for (const code in response) {
          this.companyUserMap[code] = [];
          for (const user of response[code]) {
            const newUser: User = {
              ...user,
              companyCode: code
            };

            this.userMap[user.email] = newUser;
            this.companyUserMap[code].push(newUser);
          }
        }
        resolve(true);
      });
    });
  }

  addNewUser(user: User) {
    const existingUser = this.userMap[user.email];
    this.userMap[user.email] = user;
    if (!this.companyUserMap[user.companyCode]) {
      this.companyUserMap[user.companyCode] = [];
    }
    const index = this.companyUserMap[user.companyCode].push(user);

    return this.restApi.addUser({
      companyCode: user.companyCode,
      email: user.email,
      firstName: user.firstName,
      familyName: user.familyName,
      role: user.role
    }).pipe(catchError(err => {
      // Undo local changes
      this.userMap[user.email] = existingUser;
      this.companyUserMap[user.companyCode].splice(index, 1);
      return of();
    })).subscribe();
  }

  updateUser(updatedUser: User) {
    const existingUser = this.userMap[updatedUser.email];
    this.userMap[existingUser.email] = updatedUser;
    const index = this.companyUserMap[existingUser.companyCode].findIndex(companyUser => companyUser.email === existingUser.email);
    this.companyUserMap[existingUser.companyCode].splice(index, 1);

    if (!this.companyUserMap[updatedUser.companyCode]) {
      this.companyUserMap[updatedUser.companyCode] = [];
    }
    const updatedIndex = this.companyUserMap[updatedUser.companyCode].push(updatedUser);


    return this.restApi.updateUser(updatedUser).pipe(catchError(err => {

      // Undo local changes
      this.userMap[existingUser.email] = existingUser;
      this.companyUserMap[existingUser.companyCode].splice(index, 0, existingUser);
      this.companyUserMap[updatedUser.companyCode].splice(updatedIndex, 1);

      return of();
    })).subscribe();
  }

  addNewCompany(company: CompanyInfo) {
    const newCompany = new Company(company.code, company.name, company.products, []);
    this.companies.push(newCompany);
    this.companies.sort((a, b) => a.name < b.name ? -1 : 1);
    this.companyMap[newCompany.code] = newCompany;
    this.companyRoleProductMap[newCompany.code] = {};

    return this.restApi.addCompany(company).pipe(catchError(err => {

      // Undo local changes
      const index = this.companies.findIndex(existing => existing.code === company.code);
      this.companies.splice(index, 1);
      delete this.companyMap[newCompany.code];
      delete this.companyRoleProductMap[newCompany.code];
      return of();
    })).subscribe();
  }

  addNewRole(role: Role) {
    this.companyMap[role.company.code].addRole(role);
    return this.restApi.addRole(role).pipe(catchError(err => {

      // Undo local changes
      this.companyMap[role.company.code].removeRole(role);
      return of();
    })).subscribe();
  }

  updateRole(role: Role) {

    const company = this.companyMap[role.company.code];
    const existingRole = JSON.parse(JSON.stringify(company.getRole(role.name)));
    company.updateRole(role);

    return this.restApi.updateRole(role).pipe(catchError(err => {

      // Undo local changes
      company.updateRole(existingRole);

      return of();
    })).subscribe();
  }

  setCompanyProducts(company: CompanyInfo) {
    this.companyMap[company.code].products = company.products;
    return this.restApi.updateProducts(company).subscribe();
  }

  getPermissionsForCompany(code: string) {
    const targetCompany = this.companyMap[code];
    targetCompany.products;
    return this.permissionsForProducts.filter(p => targetCompany.products.includes(p.product))
      .map(p => new GroupedPermissions(p.product, p.permissions.map(pp => new PermissionNameAndValue(pp, pp))));
  }

  reinviteUser(user: User) {
    return this.restApi.inviteUser(user).subscribe();
  }

  deleteUser(user: User) {
    delete this.userMap[user.email];
    const index = this.companyUserMap[user.companyCode].findIndex(existing => existing.email === user.email);
    const existingUser = this.companyUserMap[user.companyCode].splice(index, 1)[0];
    return this.restApi.deleteUser(user).pipe(catchError(err => {
      // Undo local changes
      this.userMap[user.email] = existingUser;
      this.companyUserMap[user.companyCode].splice(index, 0, existingUser);
      return of();
    })).subscribe();
  }

  resetPassword(user: User) {
    return this.restApi.resetPassword(user).subscribe();
  }

  deleteCompany(company: CompanyInfo) {
    if (this.companyUserMap[company.code] && this.companyUserMap[company.code].length > 0) {
      this.messageService.add({
        severity: "error",
        summary: "Cannot delete a company with users",
        detail: `Remove all users from the company, then try again`
      });
      return;
    }
    const index = this.companies.findIndex(existing => existing.code === company.code);
    const existingCompany = this.companies.splice(index, 1)[0];
    const existingMap = this.companyMap[company.code];
    const existingRoleProductMap = this.companyRoleProductMap[company.code];
    delete this.companyMap[company.code];
    delete this.companyRoleProductMap[company.code];
    return this.restApi.deleteCompany(company).pipe(catchError(err => {

      // Undo local changes
      this.companies.splice(index, 0, existingCompany);
      this.companyMap[company.code] = existingMap;
      this.companyRoleProductMap[company.code] = existingRoleProductMap;
      return of();
    })).subscribe();
  }

  deleteRole(role: Role) {
    let roleInUse = false;
    if (this.companyUserMap[role.company.code] && this.companyUserMap[role.company.code].length > 0) {
      for (const user of this.companyUserMap[role.company.code]) {
        if (user.role === role.name) {
          roleInUse = true;
          break;
        }
      }
    }

    if (roleInUse) {
      this.messageService.add({
        severity: "error",
        summary: "Cannot delete a role with users",
        detail: `Remove this role from all users, then try again`,
      });
      return;
    }

    this.companyMap[role.company.code].removeRole(role);


    return this.restApi.deleteRole(role).pipe(catchError(err => {


      // Undo local changes
      this.companyMap[role.company.code].addRole(role);
      return of();
    })).subscribe();
  }
}